export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }).format(new Date(iso))
}

export const num = (v: number | null | undefined, places = 1): string => (v === null || v === undefined ? '—' : v.toFixed(places))

export function statusLabel(status: string): string {
  return status.replaceAll('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
}

export function statusTone(status: string): 'ok' | 'warn' | 'bad' | '' {
  if (['SCORED', 'REPORT_READY'].includes(status)) return 'ok'
  if (['WITHDRAWN', 'DISQUALIFIED'].includes(status)) return 'bad'
  if (status.includes('IN_PROGRESS') || status === 'CONSENT_PENDING') return 'warn'
  return ''
}

export const ROLE_LABELS: Record<string, string> = {
  ADMISSIONS_ADMIN: 'Admissions admin', CONTENT_ADMIN: 'Content admin', PANEL_REVIEWER: 'Panel reviewer', SUPER_ADMIN: 'Super admin',
}

/** A datetime-local input value (browser time) from an ISO instant, and back. */
export const toLocalInput = (iso: string | null): string =>
  iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''
export const fromLocalInput = (local: string): string | null => (local ? new Date(local).toISOString() : null)

/** "in 3 days", "2 hours ago": used for registration deadlines so an admin sees at a glance how long a link stays valid. */
export function relativeTime(iso: string, now = Date.now()): string {
  const diff = new Date(iso).getTime() - now
  const abs = Math.abs(diff)
  const unit = abs >= 86_400_000 ? [Math.round(abs / 86_400_000), 'day'] : abs >= 3_600_000 ? [Math.round(abs / 3_600_000), 'hour'] : [Math.max(1, Math.round(abs / 60_000)), 'minute']
  const text = `${unit[0]} ${unit[1]}${unit[0] === 1 ? '' : 's'}`
  return diff >= 0 ? `in ${text}` : `${text} ago`
}

export type LinkState = { state: 'OFF' | 'SCHEDULED' | 'OPEN' | 'CLOSED' | 'FULL'; message: string }

/** Whether a campaign's registration link is accepting people right now, and why not if it is not. */
export function linkState(c: { registrationOpen: boolean; opensAt: string | null; closesAt: string | null; registrationLimit: number | null; registeredCount: number }, now = Date.now()): LinkState {
  if (!c.registrationOpen) return { state: 'OFF', message: 'Registration is switched off. The link shows a "not open" message.' }
  if (c.opensAt && now < new Date(c.opensAt).getTime()) return { state: 'SCHEDULED', message: `Opens ${relativeTime(c.opensAt, now)} (${formatDateTime(c.opensAt)}).` }
  if (c.closesAt && now >= new Date(c.closesAt).getTime()) return { state: 'CLOSED', message: `The link expired ${relativeTime(c.closesAt, now)} (${formatDateTime(c.closesAt)}).` }
  if (c.registrationLimit !== null && c.registeredCount >= c.registrationLimit) return { state: 'FULL', message: `Full: ${c.registeredCount} of ${c.registrationLimit} places taken.` }
  const parts = [c.closesAt ? `Closes ${relativeTime(c.closesAt, now)} (${formatDateTime(c.closesAt)}).` : 'No closing date is set: the link accepts registrations until you switch it off.']
  if (c.registrationLimit !== null) parts.push(`${c.registeredCount} of ${c.registrationLimit} places taken.`)
  return { state: 'OPEN', message: parts.join(' ') }
}
